import React from "react"
import { useNavigate } from "@tanstack/react-router"
import { useAuthorizedContent } from "@/core/hooks"
import { AuthorizationGateProps } from "./AuthorizationGate.types"
import { Result, Spin, Layout, Button } from "@mimber/ui-web"
import { extractApiErrorMessage } from "@mimber/client"
import { AppHeader } from "@/components/blocks"

const { Content, Header } = Layout

const AuthorizationGateShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Layout style={{ minHeight: "100vh" }}>
    <Header
      style={{
        padding: 0,
        display: "flex",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 1,
        width: "100%",
      }}
    >
      <AppHeader />
    </Header>
    <Content
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "2rem",
        background: "var(--color-background)",
      }}
    >
      {children}
    </Content>
  </Layout>
)

/**
 * Gate component that conditionally renders children based on organization user permissions
 *
 * @example
 * ```tsx
 * // Show default access denied message
 * <AuthorizationGate resource={Resource.ORGANIZATION} action={Action.UPDATE}>
 *   <OrganizationSettings />
 * </AuthorizationGate>
 *
 * // Hide content silently
 * <AuthorizationGate resource={Resource.ORGANIZATION} action={Action.UPDATE} showFallback={false}>
 *   <OrganizationSettings />
 * </AuthorizationGate>
 *
 * // Custom fallback content
 * <AuthorizationGate resource={Resource.ORGANIZATION} action={Action.UPDATE} fallback={<div>Contact organization admin</div>}>
 *   <OrganizationSettings />
 * </AuthorizationGate>
 * ```
 */
export const AuthorizationGate: React.FC<AuthorizationGateProps> = ({
  resource,
  action,
  anyOf,
  children,
  fallback,
  showFallback = true,
  loadingComponent,
}) => {
  const navigate = useNavigate()
  const { isAuthorized, isLoading, error } = useAuthorizedContent({
    resource,
    action,
    anyOf,
  })

  if (isLoading) {
    return (
      <AuthorizationGateShell>
        {loadingComponent || (
          <div className="flex flex-col items-center text-center">
            <Spin size="large" />
            <p className="mt-4 text-muted-foreground">
              Checking permissions...
            </p>
          </div>
        )}
      </AuthorizationGateShell>
    )
  }

  if (error) {
    return (
      <AuthorizationGateShell>
        <div className="max-w-md w-full">
          <Result
            status="error"
            title="Permission Check Failed"
            subTitle={extractApiErrorMessage(
              error,
              "An error occurred while checking your permissions."
            )}
            extra={
              <Button
                type="primary"
                onClick={() => navigate({ to: "/organizations" })}
              >
                Back to Organizations
              </Button>
            }
          />
        </div>
      </AuthorizationGateShell>
    )
  }

  if (!isAuthorized) {
    // Handle silent hiding
    if (fallback === null || showFallback === false) {
      return null
    }

    // Handle custom fallback
    if (fallback !== undefined) {
      return <>{fallback}</>
    }

    // Default fallback
    return (
      <AuthorizationGateShell>
        <div className="max-w-md w-full">
          <Result
            status="error"
            title="Access Denied"
            subTitle="You do not have permission to access this content."
          />
        </div>
      </AuthorizationGateShell>
    )
  }

  return <>{children}</>
}
